import type { ApiClient } from '../api/client.js';
import { browserImageCodec, prepareImage, type ImageCodec } from '../media/image-prep.js';
import { confirmUpload, createUploadTickets, putToStorage } from '../media/media-api.js';

/**
 * Pushing a drawing's flattened PNG through the ordinary media pipeline.
 *
 * `useMediaUploads` already does prepare → ticket → PUT → confirm, but it does it as a piece of
 * React state built for a person picking files off a phone: a list of rows, per-file progress
 * bars, a Retry button. A canvas has exactly one image, produces it without being asked, and
 * needs the resulting id back to finish a save. So this is the same four steps as a plain
 * awaitable function, and every step is the media module's — nothing about how an image is
 * compressed, presigned or confirmed is decided a second time here.
 *
 * The PNG the canvas exports is re-encoded to JPEG on the way, like every other image the shop
 * uploads. That loses a little crispness on hard black lines and is the right trade anyway: this
 * picture is the card's THUMBNAIL, shown in a grid and a lightbox, while the scene beside it is
 * the thing anyone actually reopens and edits.
 */

/** A filename the shop would recognise in a gallery, derived from the card it was drawn on. */
export function sketchFileName(title: string): string {
  const stem = title
    .trim()
    .replace(/[^\p{L}\p{N}]+/gu, '-')
    .replace(/^-+|-+$/g, '');
  return `${stem === '' ? 'design' : stem.slice(0, 60).toLowerCase()}-sketch.png`;
}

export interface UploadSketchRenderingInput {
  client: ApiClient;
  /** What the picture is filed under — `design_card`, `measurement_sketch`. */
  entityType: string;
  /**
   * The record the drawing is of, when it already exists (a design card) — the PNG lands attached
   * to it directly. Omitted when the record is created AFTER the picture (a measurement sketch),
   * which then adopts the unattached upload in the same write that creates it.
   */
  entityId?: string | null;
  png: Blob;
  title: string;
  /** Test seam, exactly as `useMediaUploads` takes one: the real codec needs a real canvas. */
  codec?: ImageCodec;
  put?: typeof putToStorage;
}

/**
 * Upload the rendering and return its media id.
 *
 * Throws on any failure, and the caller is expected to CARRY ON — see the sketch dialog, which
 * saves the scene regardless. A drawing whose picture did not upload is a card that shows no
 * thumbnail until the next save; a drawing that was not saved because its picture failed is work
 * somebody has to do again.
 */
export async function uploadSketchRendering(input: UploadSketchRenderingInput): Promise<string> {
  const file = new File([input.png], sketchFileName(input.title), { type: 'image/png' });
  const prepared = await prepareImage(file, input.codec ?? browserImageCodec);

  const [ticket] = await createUploadTickets(input.client, {
    entityType: input.entityType,
    entityId: input.entityId ?? null,
    files: [
      {
        clientId: 'sketch',
        originalFilename: prepared.originalFilename,
        originalBytes: prepared.originalBytes,
        full: {
          bytes: prepared.full.blob.size,
          contentType: prepared.contentType,
          width: prepared.full.width,
          height: prepared.full.height,
        },
        thumb: {
          bytes: prepared.thumb.blob.size,
          contentType: prepared.contentType,
          width: prepared.thumb.width,
          height: prepared.thumb.height,
        },
      },
    ],
  });
  if (!ticket) throw new Error('No upload ticket was issued for the sketch');

  const put = input.put ?? putToStorage;
  await put({
    url: ticket.upload.thumb.url,
    body: prepared.thumb.blob,
    headers: ticket.upload.thumb.headers,
  });
  await put({
    url: ticket.upload.full.url,
    body: prepared.full.blob,
    headers: ticket.upload.full.headers,
  });

  const media = await confirmUpload(input.client, ticket.mediaId);
  return media.id;
}
